import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getAutoDashboard } from '../../utils/api';
import { StatusBadge, PlatformBadge, formatDateTime } from './shared';

const ItemRow = ({ item, timeLabel }) => (
  <div className="p-3 bg-black rounded-lg border border-divider">
    <div className="flex items-center gap-2 mb-1 flex-wrap">
      <PlatformBadge platform={item.platform} />
      <StatusBadge status={item.status} />
      {item.publish_result?.sandbox && (
        <span className="text-xs text-orange">sandbox</span>
      )}
    </div>
    <p className="text-white text-sm font-medium truncate">{item.title || item.topic}</p>
    {timeLabel && <p className="text-xs text-gray mt-1">{timeLabel}</p>}
  </div>
);

export const AutomationDashboard = ({ clientId, refreshKey }) => {
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await getAutoDashboard(clientId);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  if (!data) return <div className="text-gray py-12 text-center">Loading dashboard…</div>;

  const stats = [
    { label: 'Pending Approval', value: data.counts.pending_approval, color: 'text-orange', testid: 'stat-pending' },
    { label: 'Approved', value: data.counts.approved, color: 'text-lime', testid: 'stat-approved' },
    { label: 'Scheduled', value: data.counts.scheduled, color: 'text-blue-400', testid: 'stat-scheduled' },
    { label: 'Published', value: data.counts.published, color: 'text-emerald-400', testid: 'stat-published' },
  ];

  return (
    <div data-testid="automation-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="bg-card-bg border border-divider rounded-xl p-5">
            <p className={`text-3xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-gray text-sm mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <h3 className="text-white font-bold text-xl mb-3">🟠 Awaiting Approval</h3>
          <div className="space-y-2" data-testid="dashboard-pending-list">
            {data.pending.length === 0 && <p className="text-gray text-sm">Nothing pending.</p>}
            {data.pending.map((i) => <ItemRow key={i.id} item={i} />)}
          </div>
        </div>
        <div>
          <h3 className="text-white font-bold text-xl mb-3">🕐 Scheduled</h3>
          <div className="space-y-2" data-testid="dashboard-scheduled-list">
            {data.scheduled.length === 0 && <p className="text-gray text-sm">Nothing scheduled.</p>}
            {data.scheduled.map((i) => (
              <ItemRow key={i.id} item={i} timeLabel={`Publishes ${formatDateTime(i.scheduled_time)}`} />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-white font-bold text-xl mb-3">✅ Published History</h3>
          <div className="space-y-2" data-testid="dashboard-published-list">
            {data.published.length === 0 && <p className="text-gray text-sm">Nothing published yet.</p>}
            {data.published.map((i) => (
              <ItemRow key={i.id} item={i} timeLabel={`Published ${formatDateTime(i.published_time)}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
